import styled from "@emotion/styled";
import { css } from "@emotion/react";
import { spin } from "theme/animations";
import { Colors } from "theme/colors";
import { fontQuicksand } from "theme/fonts";

// primary = button that saves or takes user forward
// secondary = another main level function, cancel or previous
// tertiary = add row etc, used under tables

export type ButtonType = "primary" | "secondary" | "tertiary";

type ButtonProps = {
  $loading: boolean;
  $type: ButtonType;
};

export const StyledButton = styled.button<ButtonProps>`
  // generic layout
  position: relative;
  display: inline-flex;
  margin-right: 0.5rem;
  white-space: nowrap;
  justify-content: center;

  // animation
  transition: all 0.1s;

  // if two buttons side by side
  & ~ & {
    margin-left: 1rem;
  }

  // font
  ${fontQuicksand()}

  // font size
  font-size: 1rem;

  // padding
  padding: 0.625rem 2.25rem;
  ${(props) =>
    props.$type === "tertiary" &&
    css`
      padding: 0;
    `}

  // colors
  color: ${Colors.WHITE};
  &:hover {
  }
  &:focus {
  }
  &:disabled {
    color: ${Colors.DARK_GRAY};
  }
  ${(props) =>
    props.$type === "secondary" &&
    css`
      color: ${Colors.PURPLE};
      &:hover {
        color: ${Colors.LIGHT_PURPLE};
      }
      &:focus {
      }
      &:disabled {
        color: ${Colors.DARK_GRAY};
      }
    `};
  ${(props) =>
    props.$type === "tertiary" &&
    css`
      color: ${Colors.PURPLE};
      &:hover {
        color: ${Colors.LIGHT_PURPLE};
        text-decoration: underline;
      }
      &:focus {
      }
      &:disabled {
        color: ${Colors.DARK_GRAY};
      }
    `};

  // cursor
  cursor: pointer;
  &:disabled {
    cursor: default;
    pointer-events: none;
  }

  // background color
  background-color: ${Colors.PURPLE};
  &:hover {
    background-color: ${Colors.LIGHT_PURPLE};
  }
  &:focus {
    background-color: ${Colors.PURPLE};
  }
  &:disabled {
    background-color: ${Colors.MID_GRAY};
  }
  ${(props) =>
    props.$type === "secondary" &&
    css`
      background-color: ${Colors.WHITE};
      &:hover {
        background-color: ${Colors.WHITE};
      }
      &:focus {
        background-color: ${Colors.LIGHT_BLUE};
      }
    `};
  ${(props) =>
    props.$type === "tertiary" &&
    css`
      background-color: transparent;
      &:hover {
        background-color: transparent;
      }
      &:focus {
        background-color: transparent;
      }
      &:disabled {
        background-color: transparent;
      }
    `};

  // loading
  ${(props) =>
    props.$loading &&
    css`
      color: transparent;
    `}

  // outline
  outline: none;

  // border
  border: 0.18rem solid ${Colors.PURPLE};
  &:hover {
    border-color: ${Colors.LIGHT_PURPLE};
  }
  &:focus {
    border-color: ${Colors.MID_BLUE};
  }
  &:disabled {
    border-color: ${Colors.MID_GRAY};
  }
  ${(props) =>
    props.$type === "secondary" &&
    css`
      &:hover {
      }
      &:focus {
        border-color: ${Colors.PURPLE};
      }
    `}
  ${(props) =>
    props.$type === "tertiary" &&
    css`
      border: none;
      &:hover {
      }
      &:focus {
      }
    `}

  // border radius
  border-radius: 1.875rem;
`;

interface LoaderProps {
  $loading: boolean;
}

export const StyledLoader = styled.div<LoaderProps>`
  opacity: 0;
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  display: flex;
  justify-content: center;
  align-items: center;
  color: white;

  ${spin};

  display: none;
  transition: opacity 0.2s;

  ${(props) =>
    props.$loading &&
    css`
      display: block;
      opacity: 1;
    `}
`;

interface ContentProps {
  $loading: boolean;
}

export const StyledContent = styled.div<ContentProps>`
  display: flex;
  gap: 0.5rem;
  flex-direction: row;
  align-items: center;
  opacity: 1;

  transition: opacity 0.2s;

  ${(props) =>
    props.$loading &&
    css`
      opacity: 0;
    `}
`;
